/**
 * A job's status under the toolbar (the PDF, EPUB and DOCX exports, the bundle, the backups; PLAN.md §3, step 8): a
 * region named for the job, so the page's landmarks list it, holding the library's Banner (boxed, not the strip: a
 * strip keeps its buttons beside the words, which squeezes a long status into a narrow column on a phone; a boxed one
 * puts them under). What it says is announced by the workspace's announcers, never by the Banner (A13: said once).
 */
import Banner from '../ui/Banner.ts';
import type { BannerProps } from '../ui/Banner.ts';
import ProgressBar from '../ui/ProgressBar.ts';
import { el, whenSome } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import { component } from '../ui/solid.ts';

/** A status's tone: work under way, done, done with something to know, or stopped. The words always say which. */
export type JobTone = NonNullable<BannerProps['tone']>;

export interface JobStatusProps {
  /** The hook the tests and focus moves use (`bundle-status`). */
  readonly class: string;
  /** The region's name ("PDF export"). */
  readonly label: string;
  readonly tone: () => JobTone;
  /** What it says. */
  readonly children: Child;
  /** Its buttons (each shown while it applies). */
  readonly actions: Child;
}

/** The status region and its Banner. */
export function JobStatus(props: JobStatusProps): Child {
  return el(
    'section',
    { class: `job-status ${props.class}`, 'aria-label': props.label },
    component(Banner, {
      get tone() {
        return props.tone();
      },
      children: props.children,
      actions: props.actions,
    }),
  );
}

/** A job's count so far, or null while it has none to show. */
export type JobCount = { readonly done: number; readonly total: number } | null;

/** How far a job has come, while it counts: a quiet bar named for the job (the words beside it give the percent). */
export function JobProgress(label: string, count: () => JobCount): Child {
  return whenSome(count, (now) =>
    component(ProgressBar, {
      class: 'job-progress',
      size: 'sm',
      showValue: false,
      ariaLabel: label,
      get value() {
        return now().done;
      },
      get max() {
        return Math.max(1, now().total);
      },
    }),
  );
}
